// f:\checkin\frontend\src\screens\LoginAdminScreen\LoginAdminScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../components/Branding/Logo';
import TextInput from '../../components/FormElements/TextInput';

import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
function LoginAdminScreen() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false);
  const [initialNavDecisionMade, setInitialNavDecisionMade] = useState(false); // New state
  const [navigationCmd, setNavigationCmd] = useState(null); // New state for navigation command
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, isAuthenticated, role: authRole } = useAuth(); // authIsLoading removed

  // To track if the component is mounted, useful for async operations in useEffect
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions

    console.log('[LoginAdminScreen handleSubmit] Start. Current isAuthenticated (from useAuth):', isAuthenticated);
    logout(); // Clear any existing session
    console.log('[LoginAdminScreen handleSubmit] Called logout().');
    setIsSubmitting(true);
    setLoginAttempted(false); // Reset loginAttempted before a new attempt
    setLoginError(null);
    console.log('[LoginAdminScreen handleSubmit] Admin Login attempt with:', formData);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admins/login`, { // Assuming /api/admins/login
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const loginData = await response.json();

      if (!response.ok) {
        const errorMessage = loginData.error?.message || loginData.message || `Login failed with status: ${response.status}`;
        if (isMounted.current) {
          setLoginError(errorMessage);
        }
        throw new Error(errorMessage);
      }

      if (loginData.token && loginData.role) {
        console.log(`[LoginAdminScreen handleSubmit] Login API success. Role: ${loginData.role}. Fetching admin details...`);
        try {
          // Assuming an endpoint like /api/admins/me to get admin details
          const adminDetailsResponse = await fetch(`${API_BASE_URL}/api/admins/me`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${loginData.token}`,
            },
          });

          const fetchedAdminData = await adminDetailsResponse.json();

          if (!adminDetailsResponse.ok) {
            const adminErrorMessage = fetchedAdminData.error?.message || fetchedAdminData.message || `Failed to fetch admin details: ${adminDetailsResponse.status}`;
            if (isMounted.current) {
              setLoginError(adminErrorMessage);
            }
            throw new Error(adminErrorMessage); // This will be caught by the outer catch
          }

          console.log('[LoginAdminScreen handleSubmit] Admin details fetched:', fetchedAdminData);
          const adminObject = fetchedAdminData.admin || fetchedAdminData; // Adjust based on your API response structure

          // Prepare the admin object for AuthContext, similar to other login screens
          const authAdminObject = { ...adminObject };
          if (adminObject.username && !authAdminObject.name) { // Prefer name, fallback to username
            authAdminObject.name = adminObject.username;
          }
          if (adminObject.avatar_file_id) {
            authAdminObject.image = `${API_BASE_URL}/files/serve/${adminObject.avatar_file_id}`;
          } else {
            authAdminObject.image = null; // Or a default placeholder
          }

          // Call context login
          login(loginData.token, loginData.role, adminObject.id, authAdminObject);
          console.log(`[LoginAdminScreen handleSubmit] Called context login(). isAuthenticated should become true in context.`);
          if (isMounted.current) {
            setLoginAttempted(true); // Signal that login process is complete for the useEffect
            console.log('[LoginAdminScreen handleSubmit] Set loginAttempted to true.');
          }

        } catch (fetchAdminError) {
          console.error('[LoginAdminScreen handleSubmit] Error fetching admin details after login:', fetchAdminError.message);
          if (isMounted.current) {
            setLoginError(fetchAdminError.message || 'Failed to fetch admin data after login.');
          }
          // Error is set, finally will run.
        }
      } else {
        if (isMounted.current) {
          setLoginError('Login successful, but token or role not received from server.');
        }
        // Error is set, finally will run.
      }
    } catch (error) {
      console.error('[LoginAdminScreen handleSubmit] Catch block error:', error.message);
      if (isMounted.current && !loginError) { // Only set if not already set by specific error handling
        setLoginError(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false); // Reset submitting state
        console.log('[LoginAdminScreen handleSubmit] Finally block. isSubmitting set to false.');
      }
    }
  };

  // Simplified auth state handler
  useEffect(() => {
    if (!isMounted.current) return;

    if (isAuthenticated && authRole === 'admin') {
      console.log('[LoginAdminScreen] Authenticated as admin, checking for preserved location');

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginAdminScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        return;
      }

      console.log('[LoginAdminScreen] No preserved location found, redirecting to /home');
      navigate('/home');
    }
  }, [isAuthenticated, authRole, navigate, location.state]);

  // Handle successful login attempts
  useEffect(() => {
    if (!isMounted.current || !loginAttempted) return;

    if (isAuthenticated && authRole === 'admin') {
      console.log('[LoginAdminScreen] Login successful, checking for preserved location');

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginAdminScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        setLoginAttempted(false);
        return;
      }

      console.log('[LoginAdminScreen] No preserved location found, redirecting to /home');
      navigate('/home');
    }
    setLoginAttempted(false);
  }, [loginAttempted, isAuthenticated, authRole, navigate, location.state]);


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="col-md-6 form-side">
      <div className="form-container text-center">
        <Logo />
        <h1 className="text-success fw-bold mb-4">¡Bienvenido/a!</h1>
        {loginError && <div className="alert alert-danger" role="alert">{loginError}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <TextInput
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            ariaLabel="Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass="bi bi-envelope"
            wrapperClassName="mb-3" />
          <TextInput type={showPassword ? "text" : "password"} id="password" name="password" placeholder="Contraseña" ariaLabel="Contraseña" value={formData.password} onChange={handleChange} required disabled={isSubmitting} iconClass={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`} onIconClick={togglePasswordVisibility} iconAriaLabel="Mostrar u ocultar contraseña" wrapperClassName="mb-0" />
          <div className="text-end mt-2"><Link to="/forgot-password" className="small">¿Olvidaste tu contraseña?</Link></div>

          <div className="buttons-container d-grid gap-3 mt-5 mb-5">
            <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
              {isSubmitting ? 'Iniciando...' : 'Iniciar sesión'}
            </button>
          </div>
        </form>
        <p className="mt-3 text-muted small">protegido por reCAPTCHA · <a href="#">Privacidad</a> · <a href="#">Condiciones</a></p>
      </div>
    </div>
  );
}

export default LoginAdminScreen;